import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useContent } from "../lib/content";
import { ExternalLink, Heart, X, ImagePlus, Play } from "lucide-react";

const DESCRIPTION_DEFAULT = `Inner Dance is a guided relaxation and immersive sound journey designed to bring participants into a deep meditative state. Through a carefully curated musical session performed live using Ableton Live, attendees are gently guided into theta brainwave frequencies—a state associated with deep relaxation, creativity, and access to the subconscious mind.

In this state, the boundary between the conscious and subconscious softens, allowing participants to explore inner landscapes, release emotional blockages, and experience a profound sense of connection and clarity. Each session is unique, unfolding as a personal inner journey supported by sound, intention, and subtle guidance.`;

const PACK = [
  {
    num: "01",
    title: "Techno Set",
    desc: "Inner Pulse — hardgroove / hypnotic / tribe. Main-stage energy for the peak of the night.",
    image: "",
  },
  {
    num: "02",
    title: "Prog Set",
    desc: "Sandrita — Dark Prog / BushProg / Psychedelic. Immersive, driving flow.",
    image: "",
  },
  {
    num: "03",
    title: "Downtempo Live Set",
    desc: "Chill stages, sunrise and sunsets — times to change the vibe and start with something powerful but deep. Live Ableton performance: organic, cinematic, slow-burn.",
    image: "",
  },
  {
    num: "04",
    title: "Inner Dance Workshop",
    desc: "Guided 60–90 min theta-state journey. Transformative experience for a curated audience.",
    image: "",
    requirements: [
      "A quiet, isolated space with minimal external noise.",
      "A quality sound system for full immersion.",
      "A wireless microphone for live guidance.",
      "Mats, blankets and pillows — participants lie down comfortably so the body fully relaxes while the mind travels.",
    ],
  },
  {
    num: "05",
    title: "Fire Performance",
    desc: "A mix between fire and tribal fusion — a primal visual ritual choreographed to live music. Performed solo or alongside the live set.",
    image: "",
    video_url: "",
    video_photo: "",
  },
  {
    num: "06",
    title: "Ecstatic Dance Workshop",
    desc: "Connection through music in a more intentional way — a moment to stop the world and our thoughts for an hour and deeply connect with every part of the body.",
    image: "",
    requirements: [
      "A clean, open dancing space with wooden floor preferred.",
      "Quality sound system, well-balanced for the room size.",
      "Wireless microphone for opening / closing words.",
      "Soft / dim lighting — candles, warm tones, no harsh white light.",
      "Water station and welcoming entrance for participants.",
    ],
  },
];

export default function InnerDance() {
  const kicker = useContent("page.inner_dance.kicker", "/ 06 — FESTIVAL PACK");
  const [reqOpen, setReqOpen] = useState(null);
  const [mediaOpen, setMediaOpen] = useState(null);
  const title = useContent("page.inner_dance.title", "INNER DANCE EXPERIENCE");
  const tagline = useContent(
    "inner_dance.tagline",
    "A guided relaxation and immersive sound journey into theta brainwave frequencies — designed for festivals and intentional gatherings."
  );
  const description = useContent("inner_dance.description", DESCRIPTION_DEFAULT);
  const packTitle = useContent("page.inner_dance.festival_pack_title", "Festival Pack");
  const fireVideoUrl = useContent("inner_dance.fire.video_url", "");
  const fireVideoPoster = useContent("inner_dance.fire.video_poster", "");

  // Pack items — titles, descriptions and requirements all editable from CMS
  const p01Title = useContent("inner_dance.pack.01.title", PACK[0].title);
  const p01Desc = useContent("inner_dance.pack.01.desc", PACK[0].desc);
  const p01Media = useContent("inner_dance.pack.01.media", "");
  const p02Title = useContent("inner_dance.pack.02.title", PACK[1].title);
  const p02Desc = useContent("inner_dance.pack.02.desc", PACK[1].desc);
  const p02Media = useContent("inner_dance.pack.02.media", "");
  const p03Title = useContent("inner_dance.pack.03.title", PACK[2].title);
  const p03Desc = useContent("inner_dance.pack.03.desc", PACK[2].desc);
  const p03Media = useContent("inner_dance.pack.03.media", "");
  const p04Title = useContent("inner_dance.pack.04.title", PACK[3].title);
  const p04Desc = useContent("inner_dance.pack.04.desc", PACK[3].desc);
  const p04Media = useContent("inner_dance.pack.04.media", "");
  const p04Reqs = useContent("inner_dance.pack.04.requirements", PACK[3].requirements.join("\n"));
  const p05Title = useContent("inner_dance.pack.05.title", PACK[4].title);
  const p05Desc = useContent("inner_dance.pack.05.desc", PACK[4].desc);
  const p05Media = useContent("inner_dance.pack.05.media", "");
  const p06Title = useContent("inner_dance.pack.06.title", PACK[5].title);
  const p06Desc = useContent("inner_dance.pack.06.desc", PACK[5].desc);
  const p06Media = useContent("inner_dance.pack.06.media", "");
  const p06Reqs = useContent("inner_dance.pack.06.requirements", PACK[5].requirements.join("\n"));
  const ctaBook = useContent("inner_dance.cta_book", "Book The Pack");
  const ctaEmail = useContent("inner_dance.cta_email", "Direct Email");

  // Build pack with CMS overrides for each item
  const cmsMap = {
    "01": { title: p01Title, desc: p01Desc, media: p01Media },
    "02": { title: p02Title, desc: p02Desc, media: p02Media },
    "03": { title: p03Title, desc: p03Desc, media: p03Media },
    "04": {
      title: p04Title,
      desc: p04Desc,
      media: p04Media,
      requirements: p04Reqs.split("\n").map((l) => l.trim()).filter(Boolean),
    },
    "05": {
      title: p05Title,
      desc: p05Desc,
      media: p05Media,
      video_url: fireVideoUrl || (p05Media && /\.(mp4|mov|webm|m4v)(\?|$)/i.test(p05Media) ? p05Media : ""),
      video_photo: fireVideoPoster,
    },
    "06": {
      title: p06Title,
      desc: p06Desc,
      media: p06Media,
      requirements: p06Reqs.split("\n").map((l) => l.trim()).filter(Boolean),
    },
  };
  const pack = PACK.map((p) => ({ ...p, ...(cmsMap[p.num] || {}) }));

  return (
    <div data-testid="inner-dance-page" className="px-6 md:px-12 py-20">
      <div className="max-w-[1400px] mx-auto">
        <div className="mb-16" data-testid="id-header">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-silver-400" />
            <span className="font-title text-[0.65rem] tracking-[0.4em] text-silver-400">
              {kicker}
            </span>
          </div>
          <h1 className="font-title text-silver-glow text-4xl sm:text-5xl lg:text-7xl leading-[0.95] tracking-[0.06em] whitespace-pre-line">
            {title}
          </h1>
          <p
            className="font-body text-silver-400 text-sm md:text-base max-w-2xl mt-8 leading-relaxed"
            data-testid="id-tagline"
          >
            {tagline}
          </p>
        </div>

        {/* FESTIVAL PACK — moved above THE JOURNEY */}
        <section className="mb-20" data-testid="id-pack">
          <div className="hairline-t pt-10 mb-10 flex items-baseline justify-between">
            <h2 className="font-title text-2xl md:text-3xl tracking-[0.12em] text-silver-100">
              {packTitle}
            </h2>
            <span className="font-title text-[0.62rem] tracking-[0.3em] text-silver-600">
              / {pack.length} OFFERINGS
            </span>
          </div>

          <p className="font-body text-silver-400 text-sm max-w-2xl leading-relaxed mb-10">
            Inner Dance is offered as part of a versatile pack for festivals
            and curated gatherings. One artist — multiple ways to shape the
            audience's experience, from peak-time dancefloor to deep inner
            journey, from fire ritual to ecstatic movement.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-silver-800">
            {pack.map((p) => (
              <article
                key={p.num}
                className="bg-black p-8 flex flex-col gap-4 hover:bg-silver-900/40 transition-all duration-500 group"
                data-testid={`id-pack-${p.num}`}
              >
                <div className="font-title text-[0.62rem] tracking-[0.3em] text-silver-600">
                  / {p.num}
                </div>

                {/* IMAGE / VIDEO — small, hovers to grow 2x */}
                <div className="self-start">
                  <div
                    className="w-24 h-24 hairline overflow-hidden bg-silver-900/40 flex items-center justify-center transition-transform duration-500 ease-out origin-top-left hover:scale-[3] hover:z-20 relative"
                    data-testid={`id-image-${p.num}`}
                  >
                    {p.media && /\.(mp4|mov|webm|m4v)(\?|$)/i.test(p.media) ? (
                      <video
                        src={p.media}
                        autoPlay
                        muted
                        loop
                        playsInline
                        className="w-full h-full object-cover"
                      />
                    ) : p.media ? (
                      <img
                        src={p.media}
                        alt={p.title}
                        className="w-full h-full object-cover"
                      />
                    ) : p.image ? (
                      <img
                        src={p.image}
                        alt={p.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <ImagePlus size={20} className="text-silver-700" />
                    )}
                  </div>
                </div>

                <h3 className="font-title text-xl tracking-[0.1em] text-silver-glow mt-2">
                  {p.title}
                </h3>
                <p className="font-body text-xs text-silver-400 leading-relaxed">
                  {p.desc}
                </p>

                {/* Requirements link */}
                {p.requirements && (
                  <button
                    onClick={() => setReqOpen(p)}
                    className="font-title text-[0.62rem] tracking-[0.28em] uppercase text-silver-300 hover:text-silver-100 transition-colors self-start mt-auto pt-3 hairline-t w-full text-left"
                    data-testid={`id-req-btn-${p.num}`}
                  >
                    Requirements for {p.title} →
                  </button>
                )}

                {/* Fire performance — media link */}
                {p.video_url !== undefined && (
                  <button
                    onClick={() => setMediaOpen(p)}
                    className="font-title text-[0.62rem] tracking-[0.28em] uppercase text-silver-300 hover:text-silver-100 transition-colors self-start mt-auto pt-3 hairline-t w-full text-left flex items-center gap-2"
                    data-testid={`id-media-btn-${p.num}`}
                  >
                    <Play size={11} /> View Fire Reel →
                  </button>
                )}
              </article>
            ))}
          </div>
        </section>

        {/* THE JOURNEY — Inner Dance Experience description, now below */}
        <section
          className="hairline-t pt-10 grid md:grid-cols-12 gap-10 mb-24"
          data-testid="id-description"
        >
          <div className="md:col-span-4">
            <div className="font-title text-[0.62rem] tracking-[0.3em] text-silver-600 mb-4">
              / THE JOURNEY
            </div>
            <h2 className="font-title text-silver-glow text-2xl md:text-3xl tracking-[0.1em] leading-[1.1] mb-6">
              Inner Dance Experience
            </h2>
            <div className="w-14 h-14 hairline flex items-center justify-center text-silver-200">
              <Heart size={22} />
            </div>
          </div>
          <div className="md:col-span-8">
            {description.split("\n\n").map((p, i) => (
              <p
                key={i}
                className="font-body text-silver-200 text-base md:text-lg leading-relaxed mb-6"
              >
                {p}
              </p>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section
          className="hairline-t pt-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6"
          data-testid="id-cta"
        >
          <div>
            <div className="font-title text-[0.62rem] tracking-[0.3em] text-silver-600 mb-2">
              / BOOKING INFO
            </div>
            <p className="font-body text-silver-300 text-sm max-w-md">
              Available worldwide. Custom adaptations for your festival theme —
              inquire for fees, tech rider and availability.
            </p>
          </div>
          <div className="flex gap-4 flex-wrap">
            <Link to="/booking" className="btn-sharp" data-testid="id-cta-book">
              {ctaBook}
            </Link>
            <a
              href="mailto:soundationtechnoevents@gmail.com?subject=Inner%20Dance%20Festival%20Inquiry"
              className="btn-ghost"
              data-testid="id-cta-email"
            >
              {ctaEmail} <ExternalLink size={12} />
            </a>
          </div>
        </section>
      </div>

      {/* REQUIREMENTS MODAL */}
      {reqOpen && (
        <Modal title={`Requirements for ${reqOpen.title}`} onClose={() => setReqOpen(null)}>
          <ul className="flex flex-col gap-3 mt-2">
            {reqOpen.requirements.map((r, i) => (
              <li
                key={i}
                className="flex gap-3 font-body text-sm text-silver-300 leading-relaxed hairline-b pb-3"
              >
                <span className="font-title text-[0.6rem] tracking-[0.3em] text-silver-600 shrink-0 pt-1">
                  / {String(i + 1).padStart(2, "0")}
                </span>
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </Modal>
      )}

      {/* FIRE MEDIA MODAL */}
      {mediaOpen && (
        <Modal title={`${mediaOpen.title} — Reel`} onClose={() => setMediaOpen(null)}>
          <div className="flex flex-col gap-4 mt-2">
            {mediaOpen.video_url ? (
              <div className="aspect-video hairline overflow-hidden bg-black">
                <video src={mediaOpen.video_url} controls className="w-full h-full" />
              </div>
            ) : (
              <div className="aspect-video hairline bg-silver-900/40 flex flex-col items-center justify-center gap-3 text-silver-500">
                <Play size={36} />
                <span className="font-title text-[0.62rem] tracking-[0.3em]">
                  REEL COMING SOON
                </span>
              </div>
            )}
            {mediaOpen.video_photo && (
              <img
                src={mediaOpen.video_photo}
                alt=""
                className="w-full h-auto hairline"
              />
            )}
            <p className="font-body text-xs text-silver-500 leading-relaxed">
              Full performance reel available on request — contact via Booking.
            </p>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-10 bg-black/85 backdrop-blur-md animate-fade-up"
      onClick={onClose}
      data-testid="id-modal"
    >
      <div
        className="relative max-w-lg w-full bg-black hairline p-8 max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-silver-500 hover:text-silver-100 transition-colors"
          aria-label="Close"
          data-testid="id-modal-close"
        >
          <X size={20} />
        </button>
        <div className="font-title text-[0.62rem] tracking-[0.3em] text-silver-500 mb-2">
          / DETAILS
        </div>
        <h3 className="font-title text-xl md:text-2xl tracking-[0.1em] text-silver-100">
          {title}
        </h3>
        {children}
      </div>
    </div>
  );
}
